import React, { useEffect, useRef } from "react";

/**
 * Canvas 2D atmospheric particles with soft flow-field motion.
 * ~180 particles with fading trails, phosphor yellow.
 * Respects prefers-reduced-motion.
 */
export default function ParticleCanvas() {
    const ref = useRef(null);

    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d", { alpha: true });
        const dpr = Math.min(window.devicePixelRatio || 1, 2);

        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        let w = 0, h = 0;
        const setSize = () => {
            const rect = canvas.getBoundingClientRect();
            w = rect.width; h = rect.height;
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        setSize();

        // Precomputed pseudo-noise: 2D grid of angles, interpolated at runtime
        const NX = 12, NY = 8;
        const grid = new Float32Array(NX * NY);
        for (let i = 0; i < grid.length; i++) {
            grid[i] = Math.sin(i * 12.9898) * Math.cos(i * 78.233) * Math.PI * 2;
        }
        const angleAt = (x, y, t) => {
            const gx = (x / w) * (NX - 1);
            const gy = (y / h) * (NY - 1);
            const ix = Math.min(NX - 2, Math.max(0, Math.floor(gx)));
            const iy = Math.min(NY - 2, Math.max(0, Math.floor(gy)));
            const fx = gx - ix, fy = gy - iy;
            const a = grid[iy * NX + ix];
            const b = grid[iy * NX + ix + 1];
            const c = grid[(iy + 1) * NX + ix];
            const d = grid[(iy + 1) * NX + ix + 1];
            const top = a * (1 - fx) + b * fx;
            const bot = c * (1 - fx) + d * fx;
            return top * (1 - fy) + bot * fy + t * 0.05;
        };

        const N = 180;
        const parts = new Array(N).fill(0).map(() => ({
            x: Math.random() * w,
            y: Math.random() * h,
            vx: 0, vy: 0,
            life: Math.random() * 1000,
            maxLife: 600 + Math.random() * 900,
            size: 0.6 + Math.random() * 1.6,
            opacity: 0.08 + Math.random() * 0.32,
        }));

        let t = 0;
        let running = true;
        let raf = 0;

        const tick = () => {
            if (!running) return;
            t += 1;
            // slight trailing fade — clear with alpha for fade-out trails
            ctx.fillStyle = "rgba(6, 5, 7, 0.16)";
            ctx.fillRect(0, 0, w, h);
            ctx.globalCompositeOperation = "lighter";

            for (let i = 0; i < N; i++) {
                const p = parts[i];
                const a = angleAt(p.x, p.y, t);
                const speed = 0.35;
                p.vx = p.vx * 0.94 + Math.cos(a) * speed * 0.06;
                p.vy = p.vy * 0.94 + Math.sin(a) * speed * 0.06;
                p.x += p.vx;
                p.y += p.vy;
                p.life += 1;

                if (p.x < -20 || p.x > w + 20 || p.y < -20 || p.y > h + 20 || p.life > p.maxLife) {
                    p.x = Math.random() * w;
                    p.y = Math.random() * h;
                    p.vx = 0; p.vy = 0;
                    p.life = 0;
                    p.maxLife = 600 + Math.random() * 900;
                    p.opacity = 0.08 + Math.random() * 0.32;
                    p.size = 0.6 + Math.random() * 1.6;
                }

                const lifeFade = Math.sin((p.life / p.maxLife) * Math.PI);
                const alpha = p.opacity * lifeFade;

                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(229, 255, 0, ${alpha})`;
                ctx.fill();
            }
            ctx.globalCompositeOperation = "source-over";
            raf = requestAnimationFrame(tick);
        };

        if (!reduce) {
            raf = requestAnimationFrame(tick);
        } else {
            // Draw one static frame
            ctx.fillStyle = "#060507";
            ctx.fillRect(0, 0, w, h);
            for (const p of parts) {
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(229, 255, 0, ${p.opacity * 0.6})`;
                ctx.fill();
            }
        }

        const onResize = () => setSize();
        window.addEventListener("resize", onResize);

        return () => {
            running = false;
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", onResize);
        };
    }, []);

    return <canvas ref={ref} className="lp-hero-canvas" aria-hidden="true" />;
}
